import sessionApi, { listResults } from './apiClient';
import type {
  ArchiveBatch,
  ArchiveBatchItem,
  ArchiveDocument,
  ArchiveRecord,
  ArchiveRecordType,
} from '../types/domain';

export interface ArchiveRecordFilters {
  record_type?: ArchiveRecordType;
  department?: number;
  is_archived?: boolean;
  is_legal_hold?: boolean;
  archived_after?: string;
  archived_before?: string;
  policy_code?: string;
  search?: string;
}

export interface ArchiveActionPayload {
  record_type: ArchiveRecordType;
  record_id: number;
  reason: string;
}

export interface ArchiveBatchRunPayload {
  record_types?: ArchiveRecordType[];
  limit?: number;
  confirm_execute?: boolean;
}

export interface ArchiveEligibility {
  eligible: boolean;
  reason_code: string;
  details: Record<string, any>;
  message?: string;
}

export const archiveService = {
  getArchiveRecords: async (filters: ArchiveRecordFilters = {}): Promise<ArchiveRecord[]> => {
    const response = await sessionApi.get('/archive-records/', { params: filters });
    return listResults<ArchiveRecord>(response.data);
  },

  getArchiveRecord: async (id: number): Promise<ArchiveRecord> => {
    const response = await sessionApi.get(`/archive-records/${id}/`);
    return response.data;
  },

  getArchiveRecordDocuments: async (id: number): Promise<ArchiveDocument[]> => {
    const response = await sessionApi.get(`/archive-records/${id}/documents/`);
    return response.data;
  },

  checkEligibility: async (recordType: ArchiveRecordType, recordId: number): Promise<ArchiveEligibility> => {
    const response = await sessionApi.get('/archive-records/eligibility/', {
      params: { record_type: recordType, record_id: recordId },
    });
    return response.data;
  },

  archiveRecord: async (payload: ArchiveActionPayload): Promise<ArchiveRecord> => {
    const response = await sessionApi.post('/archive-records/archive/', payload);
    return response.data;
  },

  restoreRecord: async (id: number, reason: string): Promise<ArchiveRecord> => {
    const response = await sessionApi.post(`/archive-records/${id}/restore/`, { reason });
    return response.data;
  },

  applyLegalHold: async (payload: ArchiveActionPayload): Promise<ArchiveRecord> => {
    const response = await sessionApi.post('/archive-records/legal-hold/', payload);
    return response.data;
  },

  releaseLegalHold: async (id: number, reason: string): Promise<ArchiveRecord> => {
    const response = await sessionApi.post(`/archive-records/${id}/release-legal-hold/`, { reason });
    return response.data;
  },

  getArchiveBatches: async (): Promise<ArchiveBatch[]> => {
    const response = await sessionApi.get('/archive-batches/');
    return listResults<ArchiveBatch>(response.data);
  },

  getArchiveBatchItems: async (id: number): Promise<ArchiveBatchItem[]> => {
    const response = await sessionApi.get(`/archive-batches/${id}/items/`);
    return listResults<ArchiveBatchItem>(response.data);
  },

  dryRunBatch: async (payload: ArchiveBatchRunPayload = {}): Promise<ArchiveBatch> => {
    const response = await sessionApi.post('/archive-batches/dry-run/', payload);
    return response.data;
  },

  executeBatch: async (payload: ArchiveBatchRunPayload = {}): Promise<ArchiveBatch> => {
    const response = await sessionApi.post('/archive-batches/execute/', payload);
    return response.data;
  },
};

export default archiveService;
